import { useTranslation } from '../../../shared/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Button, Space, Tag, Typography, Popconfirm, message, Collapse, Input } from 'antd';
import { UploadOutlined, DeleteOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import webmailService from '../../services/webmailService';
import DnsOneClick from '../../components/DnsOneClick';

const { Text } = Typography;
const ok = (r) => r && (r.code === 0 || r.success);
const readText = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsText(file);
});

/** Brand logo next to this domain's mail (BIMI): upload SVG → one-click DNS (BIMI TXT + enforcing DMARC). */
const BimiCard = ({ id, domainName }) => {
    const { t } = useTranslation();
    const [st, setSt] = useState(null);
    const [busy, setBusy] = useState('');
    const [vmc, setVmc] = useState('');
    const [ver, setVer] = useState(0); // refresh the preview after a new upload
    const fileRef = useRef(null);
    const vmcRef = useRef(null);

    const load = useCallback(async () => {
        const r = await webmailService.getBimi(id);
        if (ok(r)) setSt(r.data);
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const save = async (payload, key) => {
        setBusy(key);
        const r = await webmailService.saveBimi(id, payload);
        setBusy('');
        if (!ok(r)) return message.error(r?.error || r?.message || t('Operation failed'));
        setSt((s) => ({ ...s, ...r.data }));
        setVer((v) => v + 1);
        message.success(t('Saved'));
    };
    const onLogo = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        // base64: raw SVG markup in a request body trips the edge WAF's XSS rules
        if (f) save({ svg_b64: btoa(unescape(encodeURIComponent(await readText(f)))), brand: st?.brand || '' }, 'logo');
    };
    const onVmcFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (f) setVmc(await readText(f));
    };
    const remove = async () => {
        setBusy('del');
        const r = await webmailService.deleteBimi(id);
        setBusy('');
        if (ok(r)) { setSt((s) => ({ ...s, configured: false, logo_url: '', has_vmc: false, bimi_published: false })); message.success(t('Removed')); }
        else message.error(r?.error || t('Operation failed'));
    };

    const check = (good, label) => (
        <Tag color={good ? 'green' : 'orange'} icon={good ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}>{label}</Tag>
    );

    return (
        <Card size="small" title={t('Brand logo (BIMI)')} style={{ marginBottom: 16 }}
            extra={st?.configured ? (
                <Popconfirm title={t('Remove the brand logo?')} onConfirm={remove} okText={t('Remove')} okButtonProps={{ danger: true }}>
                    <Button size="small" danger type="text" icon={<DeleteOutlined />} loading={busy === 'del'} />
                </Popconfirm>
            ) : null}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                {t('Shows your logo next to mail from {{domain}} in Yahoo, Apple Mail, Gmail and others.', { domain: domainName })}
            </Text>
            <Space align="center" size={16} wrap style={{ marginBottom: 12 }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', background: 'rgba(128,128,128,0.12)', flexShrink: 0 }}>
                    {st?.logo_url && <img src={`${st.logo_url}?v=${ver}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                </div>
                <Space direction="vertical" size={6}>
                    <Button icon={<UploadOutlined />} loading={busy === 'logo'} onClick={() => fileRef.current?.click()}>
                        {st?.configured ? t('Replace logo (SVG)') : t('Upload logo (SVG)')}
                    </Button>
                    {st && (
                        <Space size={4} wrap>
                            {check(st.dmarc_enforcing, st.dmarc_enforcing ? t('DMARC enforcing') : t('DMARC must be quarantine or reject'))}
                            {check(st.bimi_published, st.bimi_published ? t('BIMI record published') : t('BIMI record not published yet'))}
                            {st.configured && (st.has_vmc ? <Tag color="blue">VMC</Tag> : <Tag>{t('No VMC (Gmail/Apple need one)')}</Tag>)}
                        </Space>
                    )}
                </Space>
                <input ref={fileRef} type="file" accept=".svg,image/svg+xml" style={{ display: 'none' }} onChange={onLogo} />
            </Space>

            {st?.configured && (
                <>
                    <DnsOneClick app="webmail_bimi" id={id} autoCheck={false} onApplied={() => setTimeout(load, 3000)} />
                    <Collapse ghost size="small" style={{ marginTop: 8 }} items={[{
                        key: 'vmc',
                        label: t('Certificate (VMC / CMC, optional)'),
                        children: (
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Text type="secondary">{t('Gmail and Apple Mail only show logos that have a VMC or CMC certificate from a certificate authority.')}</Text>
                                <Input.TextArea rows={4} value={vmc} onChange={(e) => setVmc(e.target.value)} placeholder="-----BEGIN CERTIFICATE-----" style={{ fontFamily: 'monospace', fontSize: 12 }} />
                                <Space wrap>
                                    <Button onClick={() => vmcRef.current?.click()}>{t('Choose file')}</Button>
                                    <Button type="primary" disabled={!vmc.trim()} loading={busy === 'vmc'} onClick={() => save({ vmc }, 'vmc').then(() => setVmc(''))}>{t('Save certificate')}</Button>
                                    {st.has_vmc && <Button danger loading={busy === 'vmcdel'} onClick={() => save({ vmc: '' }, 'vmcdel')}>{t('Remove certificate')}</Button>}
                                </Space>
                                <input ref={vmcRef} type="file" accept=".pem,.crt,.cer" style={{ display: 'none' }} onChange={onVmcFile} />
                            </Space>
                        ),
                    }]} />
                </>
            )}
        </Card>
    );
};

export default BimiCard;
